/**
 * 主体编辑页面
 * 添加/编辑投标主体公司信息，支持营业执照识别
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  CloudArrowUpIcon, 
  BuildingOfficeIcon,
  IdentificationIcon,
  UserIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  DocumentTextIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { companyApi, tokenApi } from '../services/api';
import LoadingOverlay from '../components/LoadingOverlay';
import Toast, { ToastType } from '../components/Toast';
import TokenAlertDialog from '../components/TokenAlertDialog';

interface CompanyFormData {
  name: string;
  fullName: string;
  unifiedSocialCreditCode: string;
  legalRepresentative: string;
  registeredCapital: string;
  establishmentDate: string;
  businessScope: string;
  registeredAddress: string;
  contactPhone: string;
  contactEmail: string;
  businessLicenseUrl?: string; // 新增：营业执照图片URL
}

const CompanyEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // 编辑模式时有id
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  // Token 欠费弹窗状态
  const [tokenAlert, setTokenAlert] = useState<{
    show: boolean;
    type: 'warning' | 'zero' | 'overdraft';
    title: string;
    message: string;
  }>({ show: false, type: 'zero', title: '', message: '' });

  const [formData, setFormData] = useState<CompanyFormData>({
    name: '',
    fullName: '',
    unifiedSocialCreditCode: '',
    legalRepresentative: '',
    registeredCapital: '',
    establishmentDate: '',
    businessScope: '',
    registeredAddress: '',
    contactPhone: '',
    contactEmail: ''
  });

  const [toast, setToast] = useState<{
    show: boolean;
    type: ToastType;
    title: string;
    messages?: string[];
  }>({
    show: false,
    type: 'info',
    title: '',
    messages: []
  });

  // 编辑模式：加载已有公司信息
  useEffect(() => {
    if (id) {
      loadCompanyData(id);
    }
  }, [id]);

  const loadCompanyData = async (companyId: string) => {
    try {
      setLoading(true);
      const response = await companyApi.getCompany(companyId);
      const data = response.data;
      
      setFormData({
        name: data.name || '',
        fullName: data.full_name || '',
        unifiedSocialCreditCode: data.unified_social_credit_code || '',
        legalRepresentative: data.legal_representative || '',
        registeredCapital: data.registered_capital || '',
        establishmentDate: data.establishment_date || '',
        businessScope: data.business_scope || '',
        registeredAddress: data.registered_address || '',
        contactPhone: data.contact_phone || '',
        contactEmail: data.contact_email || ''
      });
    } catch (error) {
      console.error('加载公司信息失败:', error);
      showToast('error', '加载失败', ['无法获取公司信息']);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (type: ToastType, title: string, messages?: string[]) => {
    setToast({ show: true, type, title, messages });
  };

  const closeToast = () => {
    setToast({ ...toast, show: false });
  };

  // 检查 Token 余额（用于营业执照识别前）
  const checkTokenBeforeOCR = async (): Promise<boolean> => {
    try {
      const response = await tokenApi.getBalance();
      const data = response.data;
      const remaining = data.remaining || 0;
      const isOverdraft = data.is_overdraft || false;
      const overdraftLimit = data.overdraft_limit || 0;

      // 检查是否欠费
      if (isOverdraft && remaining <= -overdraftLimit) {
        setTokenAlert({
          show: true,
          type: 'overdraft',
          title: '已达最大欠费额度',
          message: `您的 Token 已欠费 ${Math.abs(remaining).toLocaleString()}，已达最大欠费额度 ${overdraftLimit.toLocaleString()}，无法使用营业执照识别功能。请立即充值以恢复使用。`
        });
        return false;
      }

      if (remaining < 0) {
        setTokenAlert({
          show: true,
          type: 'zero',
          title: 'Token 已欠费',
          message: `您的 Token 已欠费 ${Math.abs(remaining).toLocaleString()}，无法使用营业执照识别功能。请立即充值。`
        });
        return false;
      }

      if (remaining === 0) {
        setTokenAlert({
          show: true,
          type: 'zero',
          title: 'Token 已用完',
          message: '您的 Token 余额为 0，无法使用营业执照识别功能。请立即充值。'
        });
        return false;
      }

      return true;
    } catch (error) {
      console.error('检查 Token 余额失败:', error);
      return true; // 检查失败时允许继续，由后端拦截
    }
  };

  // 处理表单字段变化
  const handleChange = (field: keyof CompanyFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // 处理营业执照图片上传
  const processLicenseImage = async (file: File) => {
    // 检查文件类型
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      showToast('error', '不支持的文件类型', ['请上传图片或PDF格式的营业执照']);
      return;
    }

    // 检查 Token 余额
    const hasToken = await checkTokenBeforeOCR();
    if (!hasToken) {
      return;
    }

    try {
      setLoading(true);
      setLoadingMessage('正在识别营业执照信息...');

      // 调用API识别营业执照
      const response = await companyApi.recognizeBusinessLicense(file);
      const result = response.data;
      
      if (result.success && result.data) {
        // 自动填充识别结果
        setFormData(prev => ({
          ...prev,
          name: result.data.name || prev.name,
          fullName: result.data.fullName || prev.fullName,
          unifiedSocialCreditCode: result.data.unifiedSocialCreditCode || prev.unifiedSocialCreditCode,
          legalRepresentative: result.data.legalRepresentative || prev.legalRepresentative,
          registeredCapital: result.data.registeredCapital || prev.registeredCapital,
          establishmentDate: result.data.establishmentDate || prev.establishmentDate,
          businessScope: result.data.businessScope || prev.businessScope,
          registeredAddress: result.data.registeredAddress || prev.registeredAddress,
          businessLicenseUrl: result.data.business_license_url || prev.businessLicenseUrl // 新增：保存营业执照URL
        }));
        
        showToast('success', '营业执照识别成功', ['已自动填充识别结果，请核对信息']);
      } else {
        showToast('warning', '识别结果不完整', ['请手动填写公司信息']);
      }
    } catch (error: any) {
      console.error('营业执照识别失败:', error);
      showToast('error', '识别失败', [
        error.response?.data?.detail || '请检查网络连接后重试，或手动填写信息'
      ]);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // 拖拽事件处理
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (loading) return;

    const files = e.dataTransfer.files;
    if (files.length === 0) return;

    await processLicenseImage(files[0]);
  }, [loading]);

  // 处理文件选择
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await processLicenseImage(file);
    event.target.value = '';
  };

  // 保存公司信息
  const handleSave = async () => {
    // 验证必填字段
    if (!formData.name.trim()) {
      showToast('error', '请填写公司简称');
      return;
    }
    if (!formData.fullName.trim()) {
      showToast('error', '请填写公司全称');
      return;
    }
    if (!formData.unifiedSocialCreditCode.trim()) {
      showToast('error', '请填写统一社会信用代码');
      return;
    }

    try {
      setLoading(true);
      setLoadingMessage('正在保存...');

      const saveData = {
        name: formData.name,
        full_name: formData.fullName,
        unified_social_credit_code: formData.unifiedSocialCreditCode,
        legal_representative: formData.legalRepresentative,
        registered_capital: formData.registeredCapital,
        establishment_date: formData.establishmentDate,
        business_scope: formData.businessScope,
        registered_address: formData.registeredAddress,
        contact_phone: formData.contactPhone,
        contact_email: formData.contactEmail
      };

      if (id) {
        // 编辑模式
        await companyApi.updateCompany(id, saveData);
        showToast('success', '主体信息更新成功');
      } else {
        // 新增模式
        await companyApi.createCompany(saveData);
        showToast('success', '主体添加成功');
      }

      // 触发主体更新事件，通知其他组件刷新主体状态
      window.dispatchEvent(new CustomEvent('company:updated'));

      // 返回列表页
      setTimeout(() => {
        navigate('/companies');
      }, 1000);
    } catch (error: any) {
      console.error('保存失败:', error);
      showToast('error', '保存失败', [
        error.response?.data?.detail || '请检查网络连接后重试'
      ]);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // 返回列表
  const handleBack = () => {
    navigate('/companies');
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* 全屏加载蒙层 */}
      <LoadingOverlay show={loading} message={loadingMessage} />
      
      {/* Toast 通知 */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        messages={toast.messages}
        onClose={closeToast}
      />

      {/* 头部 */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {id ? '编辑主体' : '添加主体'}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {id ? '修改投标主体企业信息' : '添加新的投标主体企业'}
              </p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={loading}
            className="inline-flex items-center px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            保存
          </button>
        </div>
      </div>

      {/* 内容区域 */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* 营业执照上传区域 */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CloudArrowUpIcon className="w-5 h-5 text-blue-600" />
              营业执照识别
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              拖拽或点击上传营业执照图片，系统将自动识别并填充公司信息
            </p>
            
            <div 
              className={`
                border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
                ${isDragging 
                  ? 'border-blue-400 bg-blue-50' 
                  : 'border-gray-300 hover:border-blue-400 bg-gray-50'
                }
              `}
              onClick={() => !loading && fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center space-y-4">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                  <CloudArrowUpIcon className="w-8 h-8 text-blue-500" />
                </div>
                <div>
                  <p className="text-gray-600 font-medium">拖拽营业执照到此处</p>
                  <p className="text-sm text-gray-400 mt-1">或点击选择文件</p>
                </div>
                <p className="text-xs text-gray-400">支持 JPG、PNG、PDF 格式</p>
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          </div>

          {/* 公司信息表单 */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <BuildingOfficeIcon className="w-5 h-5 text-blue-600" />
              公司基本信息
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 公司简称 */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  公司简称 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <BuildingOfficeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="请输入公司简称"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* 公司全称 */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  公司全称 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <BuildingOfficeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleChange('fullName', e.target.value)}
                    placeholder="请输入公司全称"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* 统一社会信用代码 */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  统一社会信用代码 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <IdentificationIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.unifiedSocialCreditCode}
                    onChange={(e) => handleChange('unifiedSocialCreditCode', e.target.value)}
                    placeholder="请输入统一社会信用代码"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* 法定代表人 */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  法定代表人
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.legalRepresentative}
                    onChange={(e) => handleChange('legalRepresentative', e.target.value)}
                    placeholder="请输入法定代表人"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* 注册资本 */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  注册资本
                </label>
                <div className="relative">
                  <CurrencyDollarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.registeredCapital}
                    onChange={(e) => handleChange('registeredCapital', e.target.value)}
                    placeholder="请输入注册资本"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* 成立日期 */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  成立日期
                </label>
                <div className="relative">
                  <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    value={formData.establishmentDate}
                    onChange={(e) => handleChange('establishmentDate', e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* 联系电话 */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  联系电话
                </label>
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => handleChange('contactPhone', e.target.value)}
                    placeholder="请输入联系电话"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* 联系邮箱 */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  联系邮箱
                </label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => handleChange('contactEmail', e.target.value)}
                    placeholder="请输入联系邮箱"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* 注册地址 */}
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium text-gray-700">
                  注册地址
                </label>
                <div className="relative">
                  <MapPinIcon className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <textarea
                    value={formData.registeredAddress}
                    onChange={(e) => handleChange('registeredAddress', e.target.value)}
                    placeholder="请输入注册地址"
                    rows={2}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                  />
                </div>
              </div>

              {/* 经营范围 */}
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium text-gray-700">
                  经营范围
                </label>
                <div className="relative">
                  <DocumentTextIcon className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <textarea
                    value={formData.businessScope}
                    onChange={(e) => handleChange('businessScope', e.target.value)}
                    placeholder="请输入经营范围"
                    rows={6}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Token 欠费弹窗 */}
      <TokenAlertDialog
        isOpen={tokenAlert.show}
        type={tokenAlert.type}
        title={tokenAlert.title}
        message={tokenAlert.message}
        onRecharge={() => {
          setTokenAlert(prev => ({ ...prev, show: false }));
          navigate('/tokens?openRecharge=true');
        }}
        onClose={() => setTokenAlert(prev => ({ ...prev, show: false }))}
      />
    </div>
  );
};

export default CompanyEdit;
